import type { AuthProvider } from '@refinedev/core'
import type { PlatformRole } from '@sirius/contracts/admin'
import type { AdminClient } from '~/api/adminClient'
import type { AdminAuthClient } from '~/auth/authClient'
import { errorText, paramsOf } from '~/i18n/errorText'
import { meInput } from '~/tenancy/hostSlug'

export const LOGIN_PATH = '/login'

/**
 * Query param carrying the path to return to after login. Not `to`: Refine's
 * `useLogin` reads `?to=` itself and navigates there unchecked.
 */
export const RETURN_TO_PARAM = 'returnTo'

/**
 * What `usePermissions` returns: the user's platform role (a platform admin
 * manages every tenant, a technician sets cabinets up), else `tenant`: tenant
 * users reach only their own tenants (see "Admin authentication" in the
 * backend README).
 */
export type AdminRole = PlatformRole | 'tenant'

/** What `useGetIdentity` returns. */
export type Identity = {
  id: string
  name: string
  email: string
  avatar?: string
}

/** The fields the sign-in form sends to `useLogin`. */
export type LoginParams = {
  email: string
  password: string
  remember?: boolean
}

/** What the forgot-password form sends to `useForgotPassword`. */
export type ForgotPasswordParams = { email: string }

/**
 * What the reset form sends to `useUpdatePassword`; Refine adds the page's
 * query, so `token` comes from the mailed link (`/reset-password?token=`).
 */
export type UpdatePasswordParams = { password: string; token?: string }

export const FORGOT_PASSWORD_PATH = '/forgot-password'
export const RESET_PASSWORD_PATH = '/reset-password'
/** An invitation's mailed link: `/accept-invitation/<id>?token=…`. */
export const ACCEPT_INVITATION_PATH = '/accept-invitation'

/** A reset link without a token, used, expired or made up. */
export const INVALID_RESET_LINK =
  'This link is invalid or has expired. Ask for a new one.'

/**
 * The `name` of the error `updatePassword` answers a link that cannot reset
 * anything with (the page shows its own explanation, not the server's).
 */
export const INVALID_RESET_LINK_ERROR = 'INVALID_RESET_LINK'

/**
 * A Better Auth error in the panel's language: its body's `code` (and
 * `params`) is the catalog key `errors.<code>`; `fallback` is the
 * server's English message, else this text.
 */
function authErrorText(
  error: { code?: string; message?: string; params?: unknown },
  fallback: string,
) {
  return errorText(
    error.code,
    paramsOf(error.params),
    error.message ?? fallback,
  )
}

type LocationLike = { pathname: string; search: string }

/** `/login?returnTo=<path>`: where a 401 sends the user, so login can return. */
export function loginRedirectPath({ pathname, search }: LocationLike) {
  const to = `${pathname}${search}`
  return to === '/' || pathname === LOGIN_PATH
    ? LOGIN_PATH
    : `${LOGIN_PATH}?${RETURN_TO_PARAM}=${encodeURIComponent(to)}`
}

function hasControlCharacter(value: string) {
  return [...value].some((char) => {
    const code = char.charCodeAt(0)
    return code <= 0x1f || code === 0x7f
  })
}

/** Only same-origin absolute paths, never back to the login page. */
export function safeReturnPath(to: string | null) {
  if (
    to === null ||
    !to.startsWith('/') ||
    to[1] === '/' ||
    to[1] === '\\' ||
    hasControlCharacter(to) ||
    to === LOGIN_PATH ||
    /^\/login[/?#]/.test(to)
  ) {
    return '/'
  }
  return to
}

/** A refused reset, as `useUpdatePassword` shows it. */
function passwordNotChanged(message: string, name = 'Password not changed') {
  return {
    success: false,
    error: { name, message },
  }
}

function statusOf(error: unknown) {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }
  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode
  }
  if ('status' in error && typeof error.status === 'number') {
    return error.status
  }

  return undefined
}

/**
 * Refine auth provider backed by Better Auth: sign-in/out and the session go
 * through the Better Auth client, the role comes from the admin contract's
 * `auth.me` (the backend decides who is staff).
 */
export function createAuthProvider({
  authClient,
  client,
  currentLocation = () => window.location,
  getMe = () => client.auth.me(meInput()),
}: {
  authClient: AdminAuthClient
  client: AdminClient
  currentLocation?: () => LocationLike
  /** `auth.me`, injectable so the app can cache it (see sessionQueries.ts) */
  getMe?: () => ReturnType<AdminClient['auth']['me']>
}): AuthProvider {
  const getSession = async () => {
    const { data } = await authClient.getSession()
    return data
  }

  return {
    login: async ({ email, password, remember }: LoginParams) => {
      const { error } = await authClient.signIn.email({
        email,
        password,
        rememberMe: remember ?? false,
      })

      if (error) {
        return {
          success: false,
          error: {
            name: 'Login failed',
            message: authErrorText(error, 'Invalid email or password'),
          },
        }
      }

      return {
        success: true,
        redirectTo: safeReturnPath(
          new URLSearchParams(currentLocation().search).get(RETURN_TO_PARAM),
        ),
      }
    },

    // the backend answers a known and an unknown email alike and mails
    // only a known one, so success says nothing about the email
    forgotPassword: async ({ email }: ForgotPasswordParams) => {
      const { error } = await authClient.requestPasswordReset({ email })

      if (error) {
        return {
          success: false,
          error: {
            name: 'Request failed',
            message: authErrorText(error, 'Could not ask for a reset link'),
          },
        }
      }

      return { success: true }
    },

    updatePassword: async ({ password, token }: UpdatePasswordParams) => {
      if (token === undefined || token === '') {
        return passwordNotChanged(INVALID_RESET_LINK, INVALID_RESET_LINK_ERROR)
      }

      const { error } = await authClient.resetPassword({
        token,
        newPassword: password,
      })

      if (error) {
        return error.code === 'INVALID_TOKEN'
          ? passwordNotChanged(INVALID_RESET_LINK, INVALID_RESET_LINK_ERROR)
          : passwordNotChanged(
              authErrorText(error, 'Could not change the password'),
            )
      }

      // the reset ended every session of the user, this browser's too
      return {
        success: true,
        redirectTo: LOGIN_PATH,
        successNotification: {
          message: 'Password changed',
          description: 'Sign in with your new password.',
        },
      }
    },

    logout: async () => {
      await authClient.signOut()
      return { success: true, redirectTo: LOGIN_PATH }
    },

    check: async () => {
      const session = await getSession()
      if (session) {
        return { authenticated: true }
      }

      return {
        authenticated: false,
        logout: true,
        redirectTo: loginRedirectPath(currentLocation()),
      }
    },

    getIdentity: async (): Promise<Identity | null> => {
      const session = await getSession()
      if (!session) {
        return null
      }

      const { id, name, email, image } = session.user
      return { id, name, email, avatar: image ?? undefined }
    },

    getPermissions: async (): Promise<AdminRole | null> => {
      try {
        const { platformRole } = await getMe()
        return platformRole ?? 'tenant'
      } catch {
        return null
      }
    },

    onError: async (error: unknown) => {
      if (statusOf(error) === 401) {
        return {
          logout: true,
          redirectTo: loginRedirectPath(currentLocation()),
          error: error as Error,
        }
      }

      return {}
    },
  }
}
